import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { conditionColor, formatListingPublishedAt, isListingReserved } from '@redevicer/shared'
import { listingsAPI } from '../api/client'
import { publicAssetUrl } from '../utils/assetUrl'
import { hasListingPhotos } from '../utils/stockPhoneImage'
import { inBoxLabelKey } from '../utils/inBoxCompleteness'
import ListingNoPhotoPlaceholder from './ListingNoPhotoPlaceholder'
import BatteryTransparencyPanel from './BatteryTransparencyPanel'
import PartsAuthenticityPanel from './PartsAuthenticityPanel'
import MyListingPhotosManager from './MyListingPhotosManager'
import ListingEditForm from './ListingEditForm'
import ListingDefectsSummary from './ListingDefectsSummary'
import ListingDetailPhotoCarousel from './ListingDetailPhotoCarousel'
import {
  resolveListingDefectMarks,
  sellerDescriptionExcludingDefects,
} from '../utils/defectMarks'

interface ListingImage {
  id: number
  thumbnail_url: string
  display_url?: string
  is_primary: boolean
}

export interface MyListingDetailListing {
  id: number
  title: string
  description?: string | null
  price: number
  condition: string
  created_at: string
  is_sold?: boolean
  is_reserved?: boolean
  reserved_for_user_id?: number | null
  battery_health?: number | null
  battery_cycles?: number | null
  battery_original?: boolean | null
  battery_tested_at?: string | null
  imei?: string
  has_factory_warranty?: boolean
  warranty_remaining?: string | null
  usage_duration?: string | null
  release_year?: number | null
  in_box_completeness?: string
  parts_authenticity?: Record<string, { status?: string; grade?: string | null }> | null
  phone_model: { manufacturer: string; model_name: string }
  images?: ListingImage[]
  defect_marks?: Array<{ x: number; y: number; label: string; photo_index?: number }> | null
}

type Props = {
  listing: MyListingDetailListing
  token: string
  editing: boolean
  busy: boolean
  soldSummary?: string
  offersSlot?: React.ReactNode
  onEdit: () => void
  onDelete: () => void
  onCancelEdit: () => void
  onSaved: () => void
  onError: (msg: string) => void
  onPhotosChange: () => void
  onReservationChange?: () => void
}

function maskImei(digits: string | undefined): string {
  if (!digits) return '—'
  const d = digits.replace(/\D/g, '')
  if (d.length >= 4) return `…${d.slice(-4)}`
  return '—'
}

function listingHeadline(listing: MyListingDetailListing): { brand: string; title: string } {
  const manufacturer = listing.phone_model.manufacturer.trim()
  const modelName = listing.phone_model.model_name.trim()
  const modelLine = `${manufacturer} ${modelName}`.trim()
  const title = listing.title.trim()
  const tLower = title.toLowerCase()
  const mLower = modelLine.toLowerCase()

  if (!title || tLower === mLower) {
    return { brand: manufacturer, title: modelName }
  }
  if (tLower.startsWith(mLower)) {
    return { brand: manufacturer, title }
  }
  return { brand: manufacturer, title }
}

export default function MyListingDetailPanel({
  listing,
  token,
  editing,
  busy,
  soldSummary,
  offersSlot,
  onEdit,
  onDelete,
  onCancelEdit,
  onSaved,
  onError,
  onPhotosChange,
  onReservationChange,
}: Props) {
  const { t, i18n } = useTranslation()
  const [activePhoto, setActivePhoto] = useState(0)
  const [reserveBusy, setReserveBusy] = useState(false)

  async function toggleListingReservation() {
    if (listing.is_sold) return
    setReserveBusy(true)
    try {
      await listingsAPI.setListingReservation(
        listing.id,
        { reserved: !listing.is_reserved, buyer_id: null },
        token,
      )
      onReservationChange?.()
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      onError(typeof detail === 'string' ? detail : t('common.error'))
    } finally {
      setReserveBusy(false)
    }
  }

  const photos = useMemo(
    () =>
      listing.images?.length
        ? [...listing.images].sort((a, b) => {
            if (a.is_primary && !b.is_primary) return -1
            if (!a.is_primary && b.is_primary) return 1
            return a.id - b.id
          })
        : [],
    [listing.images],
  )

  const hasUserPhotos = hasListingPhotos(listing.images)
  const headline = listingHeadline(listing)
  const defectMarksForSummary = resolveListingDefectMarks(
    listing.defect_marks ?? undefined,
    listing.description,
  )
  const sellerAboutText = sellerDescriptionExcludingDefects(listing.description)

  if (editing) {
    return (
      <div className="seller-listing-detail seller-listing-detail--edit">
        <ListingEditForm
          listing={listing}
          token={token}
          busy={busy}
          onBusy={() => {}}
          onCancel={onCancelEdit}
          onSaved={onSaved}
          onError={onError}
        />
      </div>
    )
  }

  return (
    <div className="seller-listing-detail listing-detail-modal listing-detail-modal--app listing-detail-modal--embedded">
      <div className="listing-detail-layout">
        <div className="listing-detail-col listing-detail-col--media">
          <div
            className={`listing-detail-gallery${
              hasUserPhotos && photos.length > 0 ? ' listing-detail-gallery--user' : ''
            }`}
          >
            {photos.length > 0 ? (
              <ListingDetailPhotoCarousel
                photos={photos}
                activePhoto={activePhoto}
                onActivePhotoChange={setActivePhoto}
                defectMarks={listing.defect_marks ?? []}
                staticPreview
              />
            ) : (
              <div className="listing-detail-main-photo">
                <ListingNoPhotoPlaceholder variant="detail" />
              </div>
            )}
            {photos.length > 1 ? (
              <div className="listing-detail-thumbs" role="tablist" aria-label={t('listing.detailGallery')}>
                {photos.map((img, idx) => (
                  <button
                    key={img.id}
                    type="button"
                    role="tab"
                    aria-selected={idx === activePhoto}
                    className={`listing-detail-thumb${idx === activePhoto ? ' listing-detail-thumb--active' : ''}`}
                    onClick={() => setActivePhoto(idx)}
                  >
                    <img
                      src={publicAssetUrl(img.display_url || img.thumbnail_url)}
                      alt=""
                      draggable={false}
                    />
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <div className="listing-detail-actions listing-detail-actions--primary listing-detail-actions--simple">
            {listing.is_sold ? (
              <p className="listing-detail-sold">{t('listing.detailSold')}</p>
            ) : (
              <div className="listing-detail-actions-cta seller-listing-detail__actions">
                <button
                  type="button"
                  className="listing-detail-buy-btn listing-detail-buy-btn--hero seller-listing-detail__edit-btn"
                  disabled={busy}
                  onClick={onEdit}
                >
                  {t('myListings.edit')}
                </button>
                <button
                  type="button"
                  className="listing-card-btn listing-card-btn--offer seller-listing-detail__delete-btn"
                  disabled={busy}
                  onClick={onDelete}
                >
                  {t('myListings.delete')}
                </button>
                <button
                  type="button"
                  className="listing-card-btn listing-card-btn--ghost seller-listing-detail__reserve-btn"
                  disabled={busy || reserveBusy}
                  onClick={() => void toggleListingReservation()}
                >
                  {reserveBusy
                    ? '…'
                    : listing.is_reserved
                      ? t('listing.unreserve')
                      : t('listing.reserveForBuyer')}
                </button>
              </div>
            )}
            <MyListingPhotosManager
              listingId={listing.id}
              token={token}
              images={listing.images ?? []}
              isSold={!!listing.is_sold}
              onChange={onPhotosChange}
            />
          </div>
        </div>

        <div className="listing-detail-col listing-detail-col--info">
          <div className="listing-detail-intro">
            {listing.is_sold ? (
              <span className="sold-badge seller-listing-detail__sold">{t('seller.sold')}</span>
            ) : isListingReserved(listing) ? (
              <span className="reserved-badge seller-listing-detail__reserved">{t('listing.reservedBadge')}</span>
            ) : null}
            <p className="listing-detail-brand">{headline.brand}</p>
            <h3 className="listing-detail-title">{headline.title}</h3>
            <p className="listing-detail-price">€{listing.price.toFixed(2)}</p>
            <span
              className="seller-listing-condition-pill seller-listing-detail__condition"
              style={{ backgroundColor: conditionColor(listing.condition) }}
            >
              {t(`listing.condition.${listing.condition}`, listing.condition)}
            </span>
          </div>

          <div className="listing-detail-body">
            <BatteryTransparencyPanel
              listing={{
                battery_health: listing.battery_health,
                battery_cycles: listing.battery_cycles,
                battery_original: listing.battery_original,
                battery_tested_at: listing.battery_tested_at,
                manufacturer: listing.phone_model.manufacturer,
              }}
            />

            <PartsAuthenticityPanel
              parts={listing.parts_authenticity}
              batteryOriginal={listing.battery_original}
              manufacturer={listing.phone_model.manufacturer}
              modelName={listing.phone_model.model_name}
            />

            <section className="listing-detail-spec-summary" aria-label={t('listing.detailDeviceInfo')}>
              <dl className="listing-detail-specs">
                <div>
                  <dt>{t('listing.publishedAt')}</dt>
                  <dd>
                    <time dateTime={listing.created_at}>
                      {formatListingPublishedAt(listing.created_at, i18n.language)}
                    </time>
                  </dd>
                </div>
                <div>
                  <dt>{t('listing.detailCondition')}</dt>
                  <dd>{t(`listing.condition.${listing.condition}`, listing.condition)}</dd>
                </div>
                {listing.in_box_completeness ? (
                  <div>
                    <dt>{t('listing.detailKit')}</dt>
                    <dd>{t(inBoxLabelKey(listing.in_box_completeness))}</dd>
                  </div>
                ) : null}
                <div>
                  <dt>{t('listing.warrantyLegend')}</dt>
                  <dd>
                    {listing.has_factory_warranty
                      ? listing.warranty_remaining
                        ? t('listing.warrantyRemainingBadge', {
                            left: t(
                              `listing.warrantyRemaining.${listing.warranty_remaining}`,
                              listing.warranty_remaining,
                            ),
                          })
                        : t('listing.warrantyBadgeYes')
                      : t('listing.warrantyBadgeNo')}
                  </dd>
                </div>
                {listing.release_year != null ? (
                  <div>
                    <dt>{t('listing.releaseYear')}</dt>
                    <dd>{listing.release_year}</dd>
                  </div>
                ) : null}
                {listing.usage_duration ? (
                  <div>
                    <dt>{t('listing.usageLegend')}</dt>
                    <dd>{t(`listing.usage.${listing.usage_duration}`, listing.usage_duration)}</dd>
                  </div>
                ) : null}
                <div>
                  <dt>{t('listing.detailImei')}</dt>
                  <dd>{maskImei(listing.imei)}</dd>
                </div>
              </dl>
            </section>

            {sellerAboutText ? (
              <section className="listing-detail-desc listing-detail-desc--top">
                <h3>{t('listing.detailAbout')}</h3>
                <p>{sellerAboutText}</p>
              </section>
            ) : null}
            {defectMarksForSummary.length > 0 ? (
              <ListingDefectsSummary
                marks={defectMarksForSummary}
                photoCount={photos.length}
                activePhoto={activePhoto}
                onSelectPhoto={setActivePhoto}
                className="listing-detail-defects-summary--in-body"
              />
            ) : null}

            {soldSummary ? <p className="listing-deal-summary">{soldSummary}</p> : null}

            {offersSlot}
          </div>
        </div>
      </div>
    </div>
  )
}
